import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import axios from 'axios';

interface DashboardStats {
  totalUsers: number;
  pendingApprovals: number;
  freelancers: number;
  referrers: number;
  managers: number;
}

interface User {
  id: string;
  email: string;
  name: string;
  username: string;
  role: 'admin' | 'manager' | 'freelancer' | 'referrer';
  is_approved: boolean;
  whatsapp_contact?: string;
  account_number?: string;
  bank_name?: string;
  account_name?: string;
  created_at: string;
}

interface GroupedUsers {
  admin: User[];
  manager: User[];
  freelancer: User[];
  referrer: User[];
}

interface CreateManagerForm {
  name: string;
  email: string;
  username: string;
  password: string;
  confirmPassword: string;
}

const AdminDashboard: React.FC = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const [activeTab, setActiveTab] = useState<'dashboard' | 'approvals' | 'users' | 'managers'>('dashboard');
  const [stats, setStats] = useState<DashboardStats | null>(null);
  const [pendingUsers, setPendingUsers] = useState<User[]>([]);
  const [allUsers, setAllUsers] = useState<User[]>([]);
  const [groupedUsers, setGroupedUsers] = useState<GroupedUsers>({
    admin: [],
    manager: [],
    freelancer: [],
    referrer: []
  });
  const [isLoading, setIsLoading] = useState(true);
  const [message, setMessage] = useState('');
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const [user, setUser] = useState<User | null>(null);
  const [isCreateManagerModalOpen, setIsCreateManagerModalOpen] = useState(false);
  const [isCreatingManager, setIsCreatingManager] = useState(false);
  const [createManagerForm, setCreateManagerForm] = useState<CreateManagerForm>({
    name: '',
    email: '',
    username: '',
    password: '',
    confirmPassword: ''
  });
  const [createManagerError, setCreateManagerError] = useState('');

  // Wrap loadUserData in useCallback to prevent unnecessary re-renders
  const loadUserData = useCallback(async () => {
    try {
      const token = localStorage.getItem('token');
      if (!token) {
        navigate('/signin');
        return;
      }

      const response = await axios.get('http://localhost:5000/api/users/profile', {
        headers: { Authorization: `Bearer ${token}` }
      });

      if (response.data.success) {
        setUser(response.data.data.user);
      }
    } catch (error) {
      console.error('Error loading user data:', error);
    }
  }, [navigate]);

  // Wrap loadDashboardData in useCallback
  const loadDashboardData = useCallback(async () => {
    try {
      setIsLoading(true);
      
      if (activeTab === 'dashboard') {
        const statsResponse = await axios.get('http://localhost:5000/api/admin/dashboard/stats', {
          headers: { Authorization: `Bearer ${localStorage.getItem('token')}` }
        });
        setStats(statsResponse.data.data);
      }
      
      if (activeTab === 'approvals') {
        const approvalsResponse = await axios.get('http://localhost:5000/api/admin/users/pending', {
          headers: { Authorization: `Bearer ${localStorage.getItem('token')}` }
        });
        setPendingUsers(approvalsResponse.data.data.users);
      }
      
      if (activeTab === 'users') {
        const usersResponse = await axios.get('http://localhost:5000/api/admin/users', {
          headers: { Authorization: `Bearer ${localStorage.getItem('token')}` }
        });
        const users: User[] = usersResponse.data.data.users;
        setAllUsers(users);
        
        // Group users by role
        const grouped: GroupedUsers = {
          admin: users.filter((user: User) => user.role === 'admin'),
          manager: users.filter((user: User) => user.role === 'manager'),
          freelancer: users.filter((user: User) => user.role === 'freelancer'),
          referrer: users.filter((user: User) => user.role === 'referrer')
        };
        setGroupedUsers(grouped);
      }
      
    } catch (error: any) {
      console.error('Error loading admin data:', error);
      setMessage(error.response?.data?.message || 'Error loading data');
    } finally {
      setIsLoading(false);
    }
  }, [activeTab]);

  // Use useEffect with proper dependencies
  useEffect(() => {
    loadUserData();
  }, [loadUserData]);

  useEffect(() => {
    loadDashboardData();
  }, [loadDashboardData]);

  const handleApproveUser = async (userId: string, userName: string) => {
    try {
      const response = await axios.post(
        `http://localhost:5000/api/admin/users/${userId}/approve`,
        {},
        { headers: { Authorization: `Bearer ${localStorage.getItem('token')}` } }
      );

      if (response.data.success) {
        setMessage(`User ${userName} has been approved successfully`);
        setPendingUsers(pendingUsers.filter(user => user.id !== userId));
        await loadDashboardData(); // Refresh stats
      }
    } catch (error: any) {
      setMessage(error.response?.data?.message || 'Error approving user');
    }
  };

  const handleRejectUser = async (userId: string, userName: string) => {
    if (!window.confirm(`Are you sure you want to reject ${userName}? This action cannot be undone.`)) return;

    try {
      const response = await axios.post(
        `http://localhost:5000/api/admin/users/${userId}/reject`,
        {},
        { headers: { Authorization: `Bearer ${localStorage.getItem('token')}` } }
      );

      if (response.data.success) {
        setMessage(`User ${userName} has been rejected`);
        setPendingUsers(pendingUsers.filter(user => user.id !== userId));
      }
    } catch (error: any) {
      setMessage(error.response?.data?.message || 'Error rejecting user');
    }
  };

  const handleLogout = () => {
    localStorage.removeItem('token');
    localStorage.removeItem('user');
    navigate('/');
  };

  const toggleMobileMenu = () => {
    setIsMobileMenuOpen(!isMobileMenuOpen);
  };

  const getRoleBadgeColor = (role: string) => {
    switch (role) {
      case 'admin': return 'role-admin';
      case 'manager': return 'role-manager';
      case 'freelancer': return 'role-freelancer';
      case 'referrer': return 'role-referrer';
      default: return 'role-admin';
    }
  };

  const getStatusBadge = (is_approved: boolean) => {
    return is_approved ? 'status-badge approved' : 'status-badge pending';
  };

  const getRoleDisplayName = (role: string) => {
    switch (role) {
      case 'admin': return 'Administrators';
      case 'manager': return 'Managers';
      case 'freelancer': return 'Freelancers';
      case 'referrer': return 'Referrers';
      default: return role;
    }
  };

  // Create Manager Functions
  const openCreateManagerModal = () => {
    setIsCreateManagerModalOpen(true);
    setCreateManagerError('');
    setCreateManagerForm({
      name: '',
      email: '',
      username: '',
      password: '',
      confirmPassword: ''
    });
  };

  const closeCreateManagerModal = () => {
    setIsCreateManagerModalOpen(false);
    setCreateManagerError('');
  };

  const handleCreateManagerChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = e.target;
    setCreateManagerForm(prev => ({
      ...prev,
      [name]: value
    }));
    if (createManagerError) setCreateManagerError('');
  };

  const validateManagerForm = (): boolean => {
    const { name, email, username, password, confirmPassword } = createManagerForm;

    if (!name.trim() || !email.trim() || !username.trim() || !password || !confirmPassword) {
      setCreateManagerError('All fields are required');
      return false;
    }

    if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email)) {
      setCreateManagerError('Please enter a valid email address');
      return false;
    }

    if (password.length < 6) {
      setCreateManagerError('Password must be at least 6 characters long');
      return false;
    }

    if (password !== confirmPassword) {
      setCreateManagerError('Passwords do not match');
      return false;
    }

    if (username.length < 3) {
      setCreateManagerError('Username must be at least 3 characters long');
      return false;
    }

    return true;
  };

  const handleCreateManager = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!validateManagerForm()) return;

    setIsCreatingManager(true);
    setCreateManagerError('');

    try {
      const response = await axios.post(
        'http://localhost:5000/api/admin/managers/create',
        {
          name: createManagerForm.name,
          email: createManagerForm.email,
          username: createManagerForm.username,
          password: createManagerForm.password
        },
        {
          headers: { Authorization: `Bearer ${localStorage.getItem('token')}` }
        }
      );

      if (response.data.success) {
        setMessage(`Manager ${response.data.data.user.name} created successfully!`);
        closeCreateManagerModal();
        await loadDashboardData(); // Refresh the data to show the new manager
      }
    } catch (error: any) {
      console.error('Error creating manager:', error);
      setCreateManagerError(
        error.response?.data?.message || 'Error creating manager. Please try again.'
      );
    } finally {
      setIsCreatingManager(false);
    }
  };

  if (isLoading && activeTab === 'dashboard') {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <div className="loading-spinner mx-auto mb-4"></div>
          <p className="text-gray-600">Loading admin dashboard...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Navigation */}
      <nav className="navbar">
        <div className="navbar-container">
          <div className="flex items-center gap-6">
            <div className="navbar-brand">
              <div className="brand-logo">NS</div>
              <span className="brand-text">NexSync</span>
            </div>
            
            {/* Desktop Navigation */}
            <div className="hidden md:flex items-center gap-6">
              <button 
                type="button"
                onClick={() => navigate('/dashboard')}
                className={`nav-link ${location.pathname === '/dashboard' ? 'nav-link-active' : ''}`}
                title="Dashboard"
              >
                Dashboard
              </button>
              <button 
                type="button"
                onClick={() => navigate('/profile')}
                className={`nav-link ${location.pathname === '/profile' ? 'nav-link-active' : ''}`}
                title="Profile"
              >
                Profile
              </button>
              <button 
                type="button"
                onClick={() => navigate('/admin')}
                className={`nav-link ${location.pathname === '/admin' ? 'nav-link-active' : ''}`}
                title="Admin Panel"
              >
                Admin Panel
              </button>
            </div>
          </div>

          <div className="flex items-center gap-4">
            {/* User Info - Desktop */}
            <div className="hidden md:flex items-center gap-3">
              <div className="text-right">
                <div className="text-sm font-medium text-gray-900">{user?.username || 'Admin'}</div>
                <div className="flex items-center gap-2">
                  <span className="role-badge role-admin">
                    Admin
                  </span>
                  <span className="status-badge approved">
                    Approved
                  </span>
                </div>
              </div>
            </div>

            {/* Mobile Menu Button */}
            <button 
              type="button"
              className="md:hidden p-2 rounded-md text-gray-600 hover:text-gray-900 hover:bg-gray-100"
              onClick={toggleMobileMenu}
              title="Toggle menu"
              aria-label="Toggle menu"
              aria-expanded={isMobileMenuOpen}
            >
              <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                {isMobileMenuOpen ? (
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                ) : (
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 6h16M4 12h16M4 18h16" />
                )}
              </svg>
            </button>

            {/* Logout Button - Desktop */}
            <button 
              type="button"
              onClick={handleLogout}
              className="hidden md:flex btn btn-ghost btn-sm"
              title="Logout"
            >
              <svg className="w-4 h-4 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 16l4-4m0 0l-4-4m4 4H7m6 4v1a3 3 0 01-3 3H6a3 3 0 01-3-3V7a3 3 0 013-3h4a3 3 0 013 3v1" />
              </svg>
              Logout
            </button>
          </div>
        </div>

        {/* Mobile Menu */}
        {isMobileMenuOpen && (
          <div className="md:hidden bg-white border-t border-gray-200">
            <div className="px-4 py-3 space-y-3">
              <button 
                type="button"
                onClick={() => {
                  navigate('/dashboard');
                  setIsMobileMenuOpen(false);
                }}
                className={`nav-link-mobile ${location.pathname === '/dashboard' ? 'nav-link-mobile-active' : ''}`}
                title="Dashboard"
              >
                <svg className="w-5 h-5 mr-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 12l2-2m0 0l7-7 7 7M5 10v10a1 1 0 001 1h3m10-11l2 2m-2-2v10a1 1 0 01-1 1h-3m-6 0a1 1 0 001-1v-4a1 1 0 011-1h2a1 1 0 011 1v4a1 1 0 001 1m-6 0h6" />
                </svg>
                Dashboard
              </button>
              
              <button 
                type="button"
                onClick={() => {
                  navigate('/profile');
                  setIsMobileMenuOpen(false);
                }}
                className={`nav-link-mobile ${location.pathname === '/profile' ? 'nav-link-mobile-active' : ''}`}
                title="Profile"
              >
                <svg className="w-5 h-5 mr-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z" />
                </svg>
                Profile
              </button>

              <button 
                type="button"
                onClick={() => {
                  navigate('/admin');
                  setIsMobileMenuOpen(false);
                }}
                className={`nav-link-mobile ${location.pathname === '/admin' ? 'nav-link-mobile-active' : ''}`}
                title="Admin Panel"
              >
                <svg className="w-5 h-5 mr-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10.325 4.317c.426-1.756 2.924-1.756 3.35 0a1.724 1.724 0 002.573 1.066c1.543-.94 3.31.826 2.37 2.37a1.724 1.724 0 001.065 2.572c1.756.426 1.756 2.924 0 3.35a1.724 1.724 0 00-1.066 2.573c.94 1.543-.826 3.31-2.37 2.37a1.724 1.724 0 00-2.572 1.065c-.426 1.756-2.924 1.756-3.35 0a1.724 1.724 0 00-2.573-1.066c-1.543.94-3.31-.826-2.37-2.37a1.724 1.724 0 00-1.065-2.572c-1.756-.426-1.756-2.924 0-3.35a1.724 1.724 0 001.066-2.573c-.94-1.543.826-3.31 2.37-2.37.996.608 2.296.07 2.572-1.065z" />
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
                </svg>
                Admin Panel
              </button>

              {/* Mobile User Info */}
              <div className="px-3 py-2 border-t border-gray-200 mt-3 pt-3">
                <div className="flex items-center gap-3">
                  <div className="flex-1">
                    <div className="text-sm font-medium text-gray-900">{user?.username || 'Admin'}</div>
                    <div className="flex items-center gap-2 mt-1">
                      <span className="role-badge role-admin">
                        Admin
                      </span>
                      <span className="status-badge approved">
                        Approved
                      </span>
                    </div>
                  </div>
                </div>
              </div>

              {/* Mobile Logout */}
              <button 
                type="button"
                onClick={() => {
                  handleLogout();
                  setIsMobileMenuOpen(false);
                }}
                className="nav-link-mobile text-red-600 hover:text-red-700 hover:bg-red-50"
                title="Logout"
              >
                <svg className="w-5 h-5 mr-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 16l4-4m0 0l-4-4m4 4H7m6 4v1a3 3 0 01-3 3H6a3 3 0 01-3-3V7a3 3 0 013-3h4a3 3 0 013 3v1" />
                </svg>
                Logout
              </button>
            </div>
          </div>
        )}
      </nav>

      {/* Admin Navigation Tabs */}
      <nav className="admin-nav">
        <div className="flex overflow-x-auto">
          <button 
            type="button"
            className={`nav-btn ${activeTab === 'dashboard' ? 'active' : ''}`}
            onClick={() => setActiveTab('dashboard')}
            title="Dashboard Overview"
          >
            <svg className="w-4 h-4 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 12l2-2m0 0l7-7 7 7M5 10v10a1 1 0 001 1h3m10-11l2 2m-2-2v10a1 1 0 01-1 1h-3m-6 0a1 1 0 001-1v-4a1 1 0 011-1h2a1 1 0 011 1v4a1 1 0 001 1m-6 0h6" />
            </svg>
            Dashboard
          </button>
          <button 
            type="button"
            className={`nav-btn ${activeTab === 'approvals' ? 'active' : ''}`}
            onClick={() => setActiveTab('approvals')}
            title="Pending Approvals"
          >
            <svg className="w-4 h-4 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
            </svg>
            Pending Approvals
            {stats && stats.pendingApprovals > 0 && (
              <span className="badge">{stats.pendingApprovals}</span>
            )}
          </button>
          <button 
            type="button"
            className={`nav-btn ${activeTab === 'users' ? 'active' : ''}`}
            onClick={() => setActiveTab('users')}
            title="All Users"
          >
            <svg className="w-4 h-4 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4.354a4 4 0 110 5.292M15 21H3v-1a6 6 0 0112 0v1zm0 0h6v-1a6 6 0 00-9-5.197m13.5-9a2.5 2.5 0 11-5 0 2.5 2.5 0 015 0z" />
            </svg>
            All Users
          </button>
          <button 
            type="button"
            className={`nav-btn ${activeTab === 'managers' ? 'active' : ''}`}
            onClick={() => setActiveTab('managers')}
            title="Manager Tools"
          >
            <svg className="w-4 h-4 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10.325 4.317c.426-1.756 2.924-1.756 3.35 0a1.724 1.724 0 002.573 1.066c1.543-.94 3.31.826 2.37 2.37a1.724 1.724 0 001.065 2.572c1.756.426 1.756 2.924 0 3.35a1.724 1.724 0 00-1.066 2.573c.94 1.543-.826 3.31-2.37 2.37a1.724 1.724 0 00-2.572 1.065c-.426 1.756-2.924 1.756-3.35 0a1.724 1.724 0 00-2.573-1.066c-1.543.94-3.31-.826-2.37-2.37a1.724 1.724 0 00-1.065-2.572c-1.756-.426-1.756-2.924 0-3.35a1.724 1.724 0 001.066-2.573c-.94-1.543.826-3.31 2.37-2.37.996.608 2.296.07 2.572-1.065z" />
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
            </svg>
            Manager Tools
          </button>
        </div>
      </nav>

      {/* Admin Content */}
      <div className="admin-content">
        {message && (
          <div className={`message ${message.includes('Error') ? 'error-message' : 'success-message'} mb-6`}>
            {message}
          </div>
        )}

        {activeTab === 'dashboard' && stats && (
          <div className="space-y-6">
            <div className="mb-6">
              <h1 className="text-3xl font-bold text-gray-900 mb-2">Admin Dashboard</h1>
              <p className="text-gray-600">Overview of system statistics and user management</p>
            </div>

            <div className="dashboard-cards">
              <div className="stat-card">
                <div className="flex items-center gap-3 mb-4">
                  <div className="w-10 h-10 bg-blue-100 rounded-lg flex items-center justify-center">
                    <svg className="w-6 h-6 text-blue-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4.354a4 4 0 110 5.292M15 21H3v-1a6 6 0 0112 0v1zm0 0h6v-1a6 6 0 00-9-5.197m13.5-9a2.5 2.5 0 11-5 0 2.5 2.5 0 015 0z" />
                    </svg>
                  </div>
                  <h3 className="text-lg font-semibold text-gray-900">Total Users</h3>
                </div>
                <div className="stat-number">{stats.totalUsers}</div>
                <p className="text-sm text-gray-600 mt-2">Registered users in the system</p>
              </div>

              <div className="stat-card warning">
                <div className="flex items-center gap-3 mb-4">
                  <div className="w-10 h-10 bg-yellow-100 rounded-lg flex items-center justify-center">
                    <svg className="w-6 h-6 text-yellow-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
                    </svg>
                  </div>
                  <h3 className="text-lg font-semibold text-gray-900">Pending Approvals</h3>
                </div>
                <div className="stat-number">{stats.pendingApprovals}</div>
                <p className="text-sm text-gray-600 mt-2">Users waiting for approval</p>
              </div>

              <div className="stat-card">
                <div className="flex items-center gap-3 mb-4">
                  <div className="w-10 h-10 bg-green-100 rounded-lg flex items-center justify-center">
                    <svg className="w-6 h-6 text-green-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 13.255A23.931 23.931 0 0112 15c-3.183 0-6.22-.62-9-1.745M16 6V4a2 2 0 00-2-2h-4a2 2 0 00-2 2v2m4 6h.01M5 20h14a2 2 0 002-2V8a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" />
                    </svg>
                  </div>
                  <h3 className="text-lg font-semibold text-gray-900">Freelancers</h3>
                </div>
                <div className="stat-number">{stats.freelancers}</div>
                <p className="text-sm text-gray-600 mt-2">Active freelancers</p>
              </div>

              <div className="stat-card">
                <div className="flex items-center gap-3 mb-4">
                  <div className="w-10 h-10 bg-purple-100 rounded-lg flex items-center justify-center">
                    <svg className="w-6 h-6 text-purple-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 10V3L4 14h7v7l9-11h-7z" />
                    </svg>
                  </div>
                  <h3 className="text-lg font-semibold text-gray-900">Referrers</h3>
                </div>
                <div className="stat-number">{stats.referrers}</div>
                <p className="text-sm text-gray-600 mt-2">Active referrers</p>
              </div>

              <div className="stat-card">
                <div className="flex items-center gap-3 mb-4">
                  <div className="w-10 h-10 bg-indigo-100 rounded-lg flex items-center justify-center">
                    <svg className="w-6 h-6 text-indigo-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10.325 4.317c.426-1.756 2.924-1.756 3.35 0a1.724 1.724 0 002.573 1.066c1.543-.94 3.31.826 2.37 2.37a1.724 1.724 0 001.065 2.572c1.756.426 1.756 2.924 0 3.35a1.724 1.724 0 00-1.066 2.573c.94 1.543-.826 3.31-2.37 2.37a1.724 1.724 0 00-2.572 1.065c-.426 1.756-2.924 1.756-3.35 0a1.724 1.724 0 00-2.573-1.066c-1.543.94-3.31-.826-2.37-2.37a1.724 1.724 0 00-1.065-2.572c-1.756-.426-1.756-2.924 0-3.35a1.724 1.724 0 001.066-2.573c-.94-1.543.826-3.31 2.37-2.37.996.608 2.296.07 2.572-1.065z" />
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
                    </svg>
                  </div>
                  <h3 className="text-lg font-semibold text-gray-900">Managers</h3>
                </div>
                <div className="stat-number">{stats.managers}</div>
                <p className="text-sm text-gray-600 mt-2">System managers</p>
              </div>
            </div>
          </div>
        )}

        {activeTab === 'approvals' && (
          <div className="approvals-section">
            <div className="mb-6">
              <h2 className="text-2xl font-bold text-gray-900 mb-2">Pending User Approvals</h2>
              <p className="text-gray-600">Review and approve user registration requests</p>
            </div>

            {isLoading ? (
              <div className="loading">Loading pending approvals...</div>
            ) : pendingUsers.length === 0 ? (
              <div className="empty-state">
                <div className="empty-state-icon">
                  <svg className="w-12 h-12 text-gray-400" fill="none" stroke="currentColor" viewBox="-38 0 100 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
                  </svg>
                </div>
                <h3 className="empty-state-title">No Pending Approvals</h3>
                <p className="empty-state-description">
                  All user registration requests have been processed. Check back later for new requests.
                </p>
              </div>
            ) : (
              <div className="users-grid">
                {pendingUsers.map((user: User) => (
                  <div key={user.id} className="user-card">
                    <div className="user-info">
                      <div className="flex items-center gap-3 mb-3">
                        <div className="user-avatar">
                          {user.name.charAt(0).toUpperCase()}
                        </div>
                        <div>
                          <h4 className="text-lg font-semibold text-gray-900">{user.name}</h4>
                          <div className="flex items-center gap-2 mt-1">
                            <span className={`role-badge ${getRoleBadgeColor(user.role)}`}>
                              {user.role}
                            </span>
                            <span className={getStatusBadge(user.is_approved)}>
                              {user.is_approved ? 'Approved' : 'Pending'}
                            </span>
                          </div>
                        </div>
                      </div>
                      
                      <div className="space-y-2">
                        <p><strong className="text-gray-700">ID:</strong> {user.id}</p>
                        <p><strong className="text-gray-700">Email:</strong> {user.email}</p>
                        <p><strong className="text-gray-700">Username:</strong> {user.username}</p>
                        {user.whatsapp_contact && (
                          <p><strong className="text-gray-700">WhatsApp:</strong> {user.whatsapp_contact}</p>
                        )}
                        <p><strong className="text-gray-700">Registered:</strong> {new Date(user.created_at).toLocaleDateString()}</p>
                      </div>
                    </div>
                    <div className="user-actions">
                      <button 
                        type="button"
                        onClick={() => handleApproveUser(user.id, user.name)}
                        className="btn btn-success btn-sm"
                        title={`Approve ${user.name}`}
                      >
                        <svg className="w-4 h-4 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                        </svg>
                        Approve
                      </button>
                      <button 
                        type="button"
                        onClick={() => handleRejectUser(user.id, user.name)}
                        className="btn btn-danger btn-sm"
                        title={`Reject ${user.name}`}
                      >
                        <svg className="w-4 h-4 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                        </svg>
                        Reject
                      </button>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        )}

        {activeTab === 'users' && (
          <div className="users-section">
            <div className="mb-6">
              <h2 className="text-2xl font-bold text-gray-900 mb-2">All Users</h2>
              <p className="text-gray-600">Manage all registered users in the system</p>
            </div>

            {isLoading ? (
              <div className="loading">Loading users...</div>
            ) : (
              <div className="space-y-8">
                {/* Display users grouped by role */}
                {Object.entries(groupedUsers).map(([role, users]) => (
                  users.length > 0 && (
                    <div key={role} className="role-section">
                      <div className="mb-4">
                        <h3 className="text-xl font-semibold text-gray-900 mb-2">
                          {getRoleDisplayName(role)} ({users.length})
                        </h3>
                        <div className="h-1 w-20 bg-blue-600 rounded"></div>
                      </div>
                      
                      <div className="users-table-container">
                        <table className="users-table">
                          <thead>
                            <tr>
                              <th>ID</th>
                              <th>Name</th>
                              <th>Email</th>
                              <th>Username</th>
                              <th>Status</th>
                              <th>Registered</th>
                            </tr>
                          </thead>
                          <tbody>
                            {users.map((user: User) => (
                              <tr key={user.id}>
                                <td className="font-mono text-sm">{user.id}</td>
                                <td className="font-medium">{user.name}</td>
                                <td>{user.email}</td>
                                <td>{user.username}</td>
                                <td>
                                  <span className={getStatusBadge(user.is_approved)}>
                                    {user.is_approved ? 'Approved' : 'Pending'}
                                  </span>
                                </td>
                                <td>{new Date(user.created_at).toLocaleDateString()}</td>
                              </tr>
                            ))}
                          </tbody>
                        </table>
                      </div>
                    </div>
                  )
                ))}
                
                {/* Show message if no users found */}
                {allUsers.length === 0 && (
                  <div className="empty-state">
                    <div className="empty-state-icon">
                      <svg className="w-12 h-12 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4.354a4 4 0 110 5.292M15 21H3v-1a6 6 0 0112 0v1zm0 0h6v-1a6 6 0 00-9-5.197m13.5-9a2.5 2.5 0 11-5 0 2.5 2.5 0 015 0z" />
                      </svg>
                    </div>
                    <h3 className="empty-state-title">No Users Found</h3>
                    <p className="empty-state-description">
                      There are no registered users in the system yet.
                    </p>
                  </div>
                )}
              </div>
            )}
          </div>
        )}

        {activeTab === 'managers' && (
          <div className="managers-section">
            <div className="mb-6">
              <h2 className="text-2xl font-bold text-gray-900 mb-2">Manager Tools</h2>
              <p className="text-gray-600">Tools for managing system administrators and permissions</p>
            </div>

            <div className="manager-actions">
              <div className="dashboard-card">
                <div className="flex items-center gap-3 mb-4">
                  <div className="w-10 h-10 bg-indigo-100 rounded-lg flex items-center justify-center">
                    <svg className="w-6 h-6 text-indigo-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M18 9v3m0 0v3m0-3h3m-3 0h-3m-2-5a4 4 0 11-8 0 4 4 0 018 0zM3 20a6 6 0 0112 0v1H3v-1z" />
                    </svg>
                  </div>
                  <h3 className="text-lg font-semibold text-gray-900">Create New Manager</h3>
                </div>
                <p className="text-gray-600 mb-4">
                  Create a new manager account with limited admin privileges for system management.
                </p>
                <button 
                  type="button"
                  className="btn btn-primary"
                  onClick={openCreateManagerModal}
                  title="Create New Manager"
                >
                  Create Manager
                </button>
              </div>
              
              <div className="dashboard-card">
                <div className="flex items-center gap-3 mb-4">
                  <div className="w-10 h-10 bg-purple-100 rounded-lg flex items-center justify-center">
                    <svg className="w-6 h-6 text-purple-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10.325 4.317c.426-1.756 2.924-1.756 3.35 0a1.724 1.724 0 002.573 1.066c1.543-.94 3.31.826 2.37 2.37a1.724 1.724 0 001.065 2.572c1.756.426 1.756 2.924 0 3.35a1.724 1.724 0 00-1.066 2.573c.94 1.543-.826 3.31-2.37 2.37a1.724 1.724 0 00-2.572 1.065c-.426 1.756-2.924 1.756-3.35 0a1.724 1.724 0 00-2.573-1.066c-1.543.94-3.31-.826-2.37-2.37a1.724 1.724 0 00-1.065-2.572c-1.756-.426-1.756-2.924 0-3.35a1.724 1.724 0 001.066-2.573c-.94-1.543.826-3.31 2.37-2.37.996.608 2.296.07 2.572-1.065z" />
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
                    </svg>
                  </div>
                  <h3 className="text-lg font-semibold text-gray-900">Manager Permissions</h3>
                </div>
                <p className="text-gray-600 mb-4">
                  Configure what managers can access and modify within the system.
                </p>
                <button 
                  type="button"
                  className="btn btn-secondary"
                  disabled
                  title="Configure Permissions (Coming Soon)"
                >
                  Configure Permissions (Coming Soon)
                </button>
              </div>
            </div>
          </div>
        )}
      </div>

      {/* Create Manager Modal */}
      {isCreateManagerModalOpen && (
        <div className="modal-overlay">
          <div className="modal-content">
            <div className="modal-header">
              <h2 className="modal-title">Create New Manager</h2>
              <button 
                type="button"
                onClick={closeCreateManagerModal}
                className="close-button"
                disabled={isCreatingManager}
                title="Close"
              >
                <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                </svg>
              </button>
            </div>

            <form onSubmit={handleCreateManager} className="modal-form">
              {createManagerError && (
                <div className="error-message mb-4">
                  <div className="flex items-center">
                    <svg className="w-4 h-4 mr-2 flex-shrink-0" fill="currentColor" viewBox="0 0 20 20">
                      <path fillRule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7-4a1 1 0 11-2 0 1 1 0 012 0zM9 9a1 1 0 000 2v3a1 1 0 001 1h1a1 1 0 100-2v-3a1 1 0 00-1-1H9z" clipRule="evenodd" />
                    </svg>
                    {createManagerError}
                  </div>
                </div>
              )}

              <div className="space-y-4">
                <div className="form-group">
                  <label htmlFor="managerName" className="form-label">
                    Full Name *
                  </label>
                  <input
                    type="text"
                    id="managerName"
                    name="name"
                    value={createManagerForm.name}
                    onChange={handleCreateManagerChange}
                    className="form-input"
                    placeholder="Enter manager's full name"
                    required
                    disabled={isCreatingManager}
                  />
                </div>

                <div className="form-group">
                  <label htmlFor="managerEmail" className="form-label">
                    Email Address *
                  </label>
                  <input
                    type="email"
                    id="managerEmail"
                    name="email"
                    value={createManagerForm.email}
                    onChange={handleCreateManagerChange}
                    className="form-input"
                    placeholder="Enter manager's email address"
                    required
                    disabled={isCreatingManager}
                  />
                </div>

                <div className="form-group">
                  <label htmlFor="managerUsername" className="form-label">
                    Username *
                  </label>
                  <input
                    type="text"
                    id="managerUsername"
                    name="username"
                    value={createManagerForm.username}
                    onChange={handleCreateManagerChange}
                    className="form-input"
                    placeholder="Choose a username"
                    required
                    disabled={isCreatingManager}
                  />
                  <div className="form-hint">
                    This will be the manager's display name
                  </div>
                </div>

                <div className="form-group">
                  <label htmlFor="managerPassword" className="form-label">
                    Password *
                  </label>
                  <input
                    type="password"
                    id="managerPassword"
                    name="password"
                    value={createManagerForm.password}
                    onChange={handleCreateManagerChange}
                    className="form-input"
                    placeholder="Create a password"
                    required
                    minLength={6}
                    disabled={isCreatingManager}
                  />
                  <div className="form-hint">
                    Password must be at least 6 characters long
                  </div>
                </div>

                <div className="form-group">
                  <label htmlFor="managerConfirmPassword" className="form-label">
                    Confirm Password *
                  </label>
                  <input
                    type="password"
                    id="managerConfirmPassword"
                    name="confirmPassword"
                    value={createManagerForm.confirmPassword}
                    onChange={handleCreateManagerChange}
                    className="form-input"
                    placeholder="Confirm the password"
                    required
                    minLength={6}
                    disabled={isCreatingManager}
                  />
                </div>
              </div>

              <div className="modal-actions">
                <button 
                  type="button" 
                  onClick={closeCreateManagerModal}
                  className="btn btn-secondary"
                  disabled={isCreatingManager}
                  title="Cancel"
                >
                  Cancel
                </button>
                <button 
                  type="submit" 
                  className="btn btn-primary"
                  disabled={isCreatingManager}
                  title="Create Manager"
                >
                  {isCreatingManager ? (
                    <span className="flex items-center">
                      <div className="loading-spinner mr-2"></div>
                      Creating...
                    </span>
                  ) : (
                    'Create Manager'
                  )}
                </button>
              </div>
            </form>

            <div className="modal-footer">
              <div className="security-tips">
                <h4 className="text-sm font-semibold text-gray-900 mb-2">Manager Account Information:</h4>
                <ul className="text-xs text-gray-600 space-y-1">
                  <li className="flex items-center">
                    ✔ Manager accounts are automatically approved
                  </li>
                  <li className="flex items-center">
                    ✔ Managers have limited admin privileges
                  </li>
                  <li className="flex items-center">
                    ✔ Managers can approve users and manage projects
                  </li>
                </ul>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default AdminDashboard;